import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/providers/AuthContext';
import { useClinicPortalServiceType } from '@/hooks/main/clinicPortalServiceType';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

interface MasterDataItem {
  id: number;
  name: string;
  value?: string | null;
  sortno?: number;
}

export interface ConsentMasterDataResponse {
  success: boolean;
  message: string;
  data: {
    incident_types: MasterDataItem[];
    incident_date_types: MasterDataItem[];
    walking_conditions: MasterDataItem[];
    walking_condition_reason_types: MasterDataItem[];
    diseases: MasterDataItem[];
    treatment_contents: MasterDataItem[];
    symptom_muscles: MasterDataItem[];
    symptom_joints: MasterDataItem[];
    hot_treatment_types: MasterDataItem[];
    massages: MasterDataItem[];
    orthomorphias: MasterDataItem[];
  };
}

export const useConsentMasterData = () => {
  const { clinic } = useAuth();
  const siteType = clinic?.site_type || 'medix';
  const clinicNumber = clinic?.clinic_number || '';
  const serviceType = useClinicPortalServiceType();

  return useQuery({
    // ConsentController@masterData returns per-service master data (X-Service-Type). With
    // staleTime: Infinity a missing service in the key means the first service seen wins for the
    // whole session. (HIT-11387)
    queryKey: ['consents', siteType, clinicNumber, 'master-data', serviceType],
    queryFn: async () => {
      const response = await apiClient.get<ConsentMasterDataResponse>(
        clinicApiPath(siteType, clinicNumber, '/consents/master-data'),
      );
      return response.data.data;
    },
    enabled: !!siteType && !!clinicNumber,
    staleTime: Infinity,
  });
};

export default useConsentMasterData;
